import { Button, Col, Divider, Form, Grid, Modal, Row, Space, Tooltip, Typography } from "@payping/web-components";
import React, { FC, useState } from "react";
import { InfoCircleOutlined } from "@payping/icons";
import { backButtonId, submitButtonId } from "@/assets/googleTrackIds";
import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import useTransferLabel from "@/utils/useTransferLabel";
import {
  CreateStepEnum,
  OverviewType,
  SettlementConfiguration,
  SettlementCreateFormType,
} from "@/pages/Settlement/Create";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import { DropdownButton } from "@/core/component";
import { useCreateSettlementModals } from "../useCreateSettlementModals";
import { SettlementRowType } from "../secondStep";

interface Props {
  loadingStepOne: boolean;
  loadingStepTwo: boolean;
  prev: () => void;
  formStepOne: FormInstance<SettlementConfiguration>;
  formStepTwo: FormInstance<SettlementRowType>;
  formStepThree: FormInstance<SettlementCreateFormType>;
  loadingDelete: boolean;
  overviewData?: OverviewType;
  setOpenModalCancel: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AnchorButtons: FC<Props> = (props) => {
  const { step } = useParams<{ step: string }>() as { step: string };
  const stepNumber = parseInt(step, 10) as CreateStepEnum;
  const { t } = useAppTranslation();
  const {
    loadingStepOne,
    loadingStepTwo,
    prev,
    loadingDelete,
    overviewData,
    setOpenModalCancel,
    formStepOne,
    formStepTwo,
    formStepThree,
  } = props;
  const { createdRequest } = useAppSelector((state) => state.cashOuts);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getLabelTransfer } = useTransferLabel();
  const navigate = useNavigate();
  const { isClickGetCode, ModalsElement, onOkAndSubmitLoading, onOkAndSubmit, loadingSubmit } =
    useCreateSettlementModals({ current: stepNumber });

  const { lg } = Grid.useBreakpoint();
  const hasEmptyFullName = createdRequest?.settlements?.some((item) => !item.fullName?.trim());

  const buttonMenuItem = [
    {
      key: 1,
      label: t("settlement.create.moreDetailsSettlement"),
    },
  ];

  const getTitleStepOne = Form.useWatch("title", formStepOne);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const ButtonElements = () => {
    switch (stepNumber) {
      case CreateStepEnum.Configuration:
        return (
          <Row gutter={[8, 8]}>
            <Col flex={2}>
              <Button loading={loadingStepOne} onClick={() => formStepOne.submit()} type={"primary"} block>
                {t("next")}
              </Button>
            </Col>
            <Col flex={1}>
              <Button
                onClick={() => {
                  if (getTitleStepOne) {
                    setOpenModalCancel(true);
                  } else {
                    navigate(DashboardRoutes.settlement.overview.url);
                  }
                }}
                block
              >
                {t("cancel")}
              </Button>
            </Col>
          </Row>
        );
      case CreateStepEnum.IBANList:
        return (
          <Row gutter={[8, 8]}>
            <Col flex={2}>
              <Button
                loading={loadingStepTwo}
                disabled={!(createdRequest && createdRequest.settlements && createdRequest.settlements.length > 0)}
                onClick={() => formStepTwo.submit()}
                type={"primary"}
                block
              >
                {(createdRequest?.errors && createdRequest?.errors?.length > 0) || loadingDelete
                  ? t("deleteAllErrors&continue")
                  : t("next")}
              </Button>
            </Col>
            <Col flex={1}>
              <Button onClick={() => prev()} block>
                {t("return")}
              </Button>
            </Col>
          </Row>
        );
      case CreateStepEnum.Verification:
        return (
          <Row gutter={[8, 8]}>
            {lg ? (
              <Col flex={2}>
                <Button
                  type={hasEmptyFullName ? "default" : "primary"}
                  danger={hasEmptyFullName}
                  block
                  onClick={async () => {
                    formStepThree.submit();
                    await onOkAndSubmit();
                  }}
                  loading={loadingSubmit || onOkAndSubmitLoading}
                  data-google-track={submitButtonId}
                >
                  {t("ok&submit")}
                </Button>
              </Col>
            ) : (
              <Col flex={2}>
                <DropdownButton
                  menu={{ items: buttonMenuItem, onClick: showModal }}
                  htmlType={"submit"}
                  type={hasEmptyFullName ? "default" : "primary"}
                  danger={hasEmptyFullName}
                  block
                  onClick={onOkAndSubmit}
                  loading={loadingSubmit || onOkAndSubmitLoading}
                  data-google-track={submitButtonId}
                >
                  {t("ok&submit")}
                </DropdownButton>
              </Col>
            )}
            <Col flex={1}>
              <Button disabled={isClickGetCode} onClick={() => prev()} block data-google-track={`${backButtonId}-3`}>
                {t("return")}
              </Button>
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ButtonElements />
      <Modal
        title={t("settlement.create.sendSettlement")}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={false}
      >
        <Space direction={"vertical"} className={"tw-w-full"} size={"middle"}>
          <Form.Item noStyle dependencies={["title"]}>
            {({ getFieldValue }) => (
              <div className={"tw-flex tw-justify-between"}>
                <div className={"tw-me-2 tw-shrink-0"}>
                  <Typography.Text type={"secondary"}>{t("settlement.create.nameList")}</Typography.Text>
                </div>

                <Typography.Text ellipsis>
                  <Tooltip title={getFieldValue("title")}>{getFieldValue("title")}</Tooltip>
                </Typography.Text>
              </div>
            )}
          </Form.Item>
          <Form.Item noStyle dependencies={["desc"]}>
            {({ getFieldValue }) => (
              <div className={"tw-flex tw-justify-between"}>
                <div className={"tw-me-2 tw-shrink-0"}>
                  <Typography.Text type={"secondary"}>{t("description")}</Typography.Text>
                </div>

                <Typography.Text ellipsis>
                  <Tooltip title={getFieldValue("desc")}>{getFieldValue("desc")}</Tooltip>
                </Typography.Text>
              </div>
            )}
          </Form.Item>
          <Form.Item noStyle dependencies={["transferType"]}>
            {({ getFieldValue }) => (
              <Row gutter={16}>
                <Col>
                  <Typography.Text type={"secondary"}>{t("settlement.create.settlementType")}</Typography.Text>
                </Col>
                <Col flex={1} className={"tw-flex tw-justify-end"}>
                  <Typography.Text>{getLabelTransfer(getFieldValue("transferType"))}</Typography.Text>
                </Col>
              </Row>
            )}
          </Form.Item>
          <Divider className={"!tw-m-0"} />
          <Form.Item noStyle shouldUpdate={(previous, next) => previous.settlement?.length !== next.settlement?.length}>
            {({ getFieldValue }) => (
              <div className={"tw-flex tw-justify-between"}>
                <div>
                  <Typography.Text type={"secondary"}>{t("settlement.create.settlementNumbers")}</Typography.Text>
                </div>
                <div>
                  <Typography.Text>
                    {getFieldValue("settlement")?.length
                      ? t("settlement.create.showLength", { length: getFieldValue("settlement").length })
                      : t("settlement.create.itemNotAdded")}
                  </Typography.Text>
                </div>
              </div>
            )}
          </Form.Item>
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <div>
                <Typography.Text type={"secondary"}>{t("settlement.totalCashout")}</Typography.Text>
              </div>
              <div>
                <Typography.Text>{overviewData?.totalAmount}</Typography.Text>
              </div>
            </div>
          </Form.Item>
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <div>
                <Typography.Text type={"secondary"}>{t("settlement.totalWage")}</Typography.Text>
              </div>
              <div>
                <Typography.Text>{overviewData?.totalWage}</Typography.Text>
              </div>
            </div>
          </Form.Item>
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <div>
                <Typography.Text type={"secondary"}>
                  <Space>
                    {t("settlement.total")}
                    <Tooltip className="tw-align-middle" placement="top" title={t("settlement.totalDescription")}>
                      <InfoCircleOutlined width={"14px"} />
                    </Tooltip>
                  </Space>
                </Typography.Text>
              </div>
              <div>
                <Typography.Text>{overviewData?.total}</Typography.Text>
              </div>
            </div>
          </Form.Item>
          <Divider className={"!tw-m-0"} />
          <Form.Item noStyle>
            <div className={"tw-flex tw-justify-between"}>
              <div>
                <Typography.Text type={"secondary"}>{t("settlement.create.remainingAmountSettlement")}</Typography.Text>
              </div>
              <div>
                <Typography.Text>{overviewData?.balance}</Typography.Text>
              </div>
            </div>
          </Form.Item>
        </Space>
      </Modal>
      {ModalsElement}
    </>
  );
};
